// src/lib/api/jobs/jobs.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getCachedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'

/**
 * Get all jobs for a company
 * Migrated from supabaseApi.js lines 691-707
 */
const _getJobs = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
    .limit(500)

  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create a new job
 * Migrated from supabaseApi.js lines 709-729
 */
const _createJob = async (jobData) => {
  validateRequired(jobData.title, 'Job title')

  const companyId = jobData.company_id || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  // Prefer the cached user id populated by the auth provider on sign-in /
  // refresh. supabase.auth.getUser() can deadlock behind supabase-js's Web
  // Lock when the auto-refresh timer was suspended mid-lock by a long
  // tab-hidden interval — that path is what made "Schedule Estimate" hang
  // silently after a tab-out until the user reloaded the page. Fall back to
  // getUser() with a 5s timeout only when the cache is cold (e.g. first call
  // after sign-in before the auth provider populated it).
  let createdBy = getCachedUserId()
  if (!createdBy) {
    const { data: { user }, error: authError } = await Promise.race([
      supabase.auth.getUser(),
      new Promise((_, reject) => setTimeout(
        () => reject(new Error('Authentication check timed out — please refresh the page')),
        5000
      ))
    ])
    if (authError || !user?.id) {
      throw new Error('Authentication required to create job')
    }
    createdBy = user.id
  }

  const sanitizedData = {
    ...jobData,
    title: sanitizeInput(jobData.title),
    notes: jobData.notes ? sanitizeInput(jobData.notes) : null,
    company_id: companyId,
    created_by: createdBy
  }

  const { data, error } = await supabase
    .from('jobs')
    .insert([sanitizedData])
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Update an existing job
 * Migrated from supabaseApi.js lines 1755-1775 (approximate)
 */
const _updateJob = async (jobId, jobData) => {
  validateRequired(jobId, 'Job ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const sanitizedData = {
    ...jobData,
    company_id: companyId
  }
  
  if (sanitizedData.title) {
    sanitizedData.title = sanitizeInput(sanitizedData.title)
  }
  if (sanitizedData.notes) {
    sanitizedData.notes = sanitizeInput(sanitizedData.notes)
  }
  
  const { data, error } = await supabase
    .from('jobs')
    .update(sanitizedData)
    .eq('id', jobId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Delete a job
 * Migrated from supabaseApi.js lines 1777-1793 (approximate)
 */
const _deleteJob = async (jobId) => {
  validateRequired(jobId, 'Job ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('jobs')
    .delete()
    .eq('id', jobId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Helper function to create a job from an estimate
 * Migrated from supabaseApi.js lines 731-750
 */
const _createJobFromEstimate = async (jobData, estimateId) => {
  const job = await _createJob(jobData)
  
  // Link the estimate to the job for easy invoice creation
  if (job.id && estimateId) {
    try {
      // Import from financial module once it's created
      const { updateEstimate } = await import('../financial/estimates')
      await updateEstimate(estimateId, { job_id: job.id })
      console.log('Linked estimate to job:', estimateId, '->', job.id)
    } catch (error) {
      console.warn('Failed to link estimate to job:', error)
      // Don't throw error - job creation succeeded, just linking failed
    }
  }
  
  return job
}

/**
 * Search jobs by query
 */
const _searchJobs = async (query, companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .or(`title.ilike.%${query.replace(/[,%\\()]/g, '')}%,notes.ilike.%${query.replace(/[,%\\()]/g, '')}%`)
    .order('created_at', { ascending: false })
    .limit(500)

  if (error) throw error
  return createSuccessResponse(data || [])
}

// Export wrapped functions with error handling
export const getJobs = withErrorHandling(_getJobs, 'fetch jobs', 'jobs')
export const createJob = withErrorHandling(_createJob, 'create job', 'job')
export const updateJob = withErrorHandling(_updateJob, 'update job', 'job')
export const deleteJob = withErrorHandling(_deleteJob, 'delete job', 'job')
export const createJobFromEstimate = withErrorHandling(_createJobFromEstimate, 'create job from estimate', 'job')
export const searchJobs = withErrorHandling(_searchJobs, 'search jobs', 'jobs')